import React, { useState, useEffect, useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import useChartColors from "Components/Common/useChartColors";
import { getYearDateRange } from "common/lib/dateUtils";
import { getPaymentsPerCategory } from "common/services/payments.service";
import useQueryState from "Components/Hooks/UseQueryState";
import { useProfile } from "Components/Hooks/UserHooks";

const groupData = (payments: any) => {
  const totalCounts = payments.map((payment: { totalcount: any }) =>
    Number(payment.totalcount)
  );
  const categories = payments.map(
    (payment: { category: any }) => payment.category
  );

  return { totalCounts, categories };
};

const PaymentCategoryChart = ({ series, labels }: any) => {
  const chartId = "portfolio_donut_charts";
  const chartColors = useChartColors(chartId);

  const options: any = {
    labels,
    chart: {
      type: "donut",
      height: 250,
    },

    plotOptions: {
      pie: {
        size: 100,
        offsetX: 0,
        offsetY: 0,
        donut: {
          size: "70%",
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: "18px",
              offsetY: -5,
            },
            value: {
              show: true,
              fontSize: "20px",
              color: "#343a40",
              fontWeight: 500,
              offsetY: 5,
              formatter: function (val: any) {
                return val.toLocaleString();
              },
            },
            total: {
              show: true,
              fontSize: "13px",
              label: "Total",
              color: "#9599ad",
              fontWeight: 500,
              formatter: function (w: any) {
                return w.globals.seriesTotals.reduce(function (a: any, b: any) {
                  return a + b;
                }, 0);
              },
            },
          },
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    legend: {
      show: false,
    },
    yaxis: {
      labels: {
        formatter: function (value: any) {
          return value + " payments/contracts";
        },
      },
    },
    stroke: {
      lineCap: "round",
      width: 2,
    },
    colors: chartColors,
  };

  return (
    <ReactApexChart
      dir="ltr"
      options={options}
      series={series}
      id={chartId}
      type="donut"
      height="270"
      data-colors='["#088F7D", "--vz-success", "--vz-warning", "#299cdb", "--vz-secondary"]'
      // data-colors-minimal='["--vz-primary", "--vz-primary-rgb, 0.85", "--vz-primary-rgb, 0.65", "--vz-primary-rgb, 0.50"]'
      // data-colors-interactive='["--vz-primary", "--vz-primary-rgb, 0.85", "--vz-primary-rgb, 0.65", "--vz-primary-rgb, 0.50"]'
      // data-colors-corporate='["--vz-primary", "--vz-secondary", "--vz-info", "--vz-success"]'
      // data-colors-galaxy='["--vz-primary", "--vz-primary-rgb, 0.85", "--vz-primary-rgb, 0.65", "--vz-primary-rgb, 0.50"]'
      className="apex-charts"
    />
  );
};

const PaymentCategory = () => {
  const { userProfile } = useProfile();
  const [payments, setPayments] = useState<[]>([]);
  const [, setLoading] = useState(false);

  const [selectedYear] = useQueryState(
    "year",
    new Date().getFullYear().toString()
  );

  const { firstDay, lastDay } = getYearDateRange(Number(selectedYear));

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      try {
        const response: any = await getPaymentsPerCategory({
          startDate: firstDay ? firstDay : undefined,
          endDate: lastDay ? lastDay : undefined,
          agencyId: userProfile?.agency?.id,
        });

        setPayments(response);
      } catch (error) {
        console.warn("Error fetching total  support", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [firstDay, lastDay, userProfile?.agency?.id]);

  const { totalCounts, categories } = useMemo(
    () => groupData(payments),
    [payments]
  );

  const dataColors = useChartColors("portfolio_donut_charts");

  return (
    <React.Fragment>
      <div className="card card-height-100">
        <div className="card-header  align-items-center d-flex">
          <h4 className="card-title mb-0 flex-grow-1">
            Distribution of payments by category
          </h4>
        </div>
        <div className="card-body">
          <PaymentCategoryChart
            labels={[
              "Advisor Salary",
              "Civil Servant base scale",
              "Civil servant + top up",
              "Top up only",
              "Other",
            ]}
            series={totalCounts}
            chartId="portfolio_donut_charts"
          />

          {categories.map((category: any, index: number) => (
            <div
              key={index}
              className="d-flex justify-content-between border-bottom border-bottom-dashed py-2"
            >
              <p className="fw-medium mb-0">
                <i
                  style={{
                    color: dataColors[index],
                  }}
                  className={`ri-checkbox-blank-circle-fill align-middle me-2`}
                ></i>{" "}
                {category}
              </p>
              <div>
                <span className="text-muted pe-5">
                  {totalCounts[index].toLocaleString() + " contracts"}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </React.Fragment>
  );
};

export default PaymentCategory;
